import { Card } from "@components/cards/card";
import { DashboardMetricCard } from "@components/layout/dashboard/metric-card";
import {
  IDashboardMetricDTO,
  InfrastructureMachinesSummaryResponse,
  PlatformAverageResponseTimeResponse,
  PlatformAvailabilityResponse,
  PlatformChecksSummaryResponse,
  PlatformHealthSummaryResponse,
} from "@hooks/dtos/IDashboardDTO";
import { Masks } from "@hooks/functions/Masks/Masks";
import { api } from "@service/api";
import { useEffect, useMemo, useState } from "react";
import {
  FaBell,
  FaCheckCircle,
  FaDatabase,
  FaExclamationTriangle,
  FaServer,
} from "react-icons/fa";
import { FiActivity, FiAlertTriangle, FiGlobe, FiWifi } from "react-icons/fi";

const DASHBOARD_HEALTH_PERIOD_HOURS = 24;
const masks = new Masks();

const operationalSummaries: IDashboardMetricDTO[] = [
  {
    title: "Plataformas em atenção",
    value: "...",
    subtitle: "Resposta alta ou instabilidade",
    icon: FaExclamationTriangle,
    variant: "warning",
  },
  {
    title: "Checks nas últimas 24h",
    value: "...",
    subtitle: "Consultas automáticas realizadas",
    icon: FiActivity,
    variant: "primary",
  },
  {
    title: "Incidentes críticos",
    value: "Em breve",
    subtitle: "Monitoramento será integrado",
    icon: FaBell,
    variant: "secondary",
  },
  {
    title: "Backups verificados",
    value: "Em breve",
    subtitle: "Rotinas serão integradas",
    icon: FaDatabase,
    variant: "secondary",
  },
];

export function Home() {
  const [infrastructureSummary, setInfrastructureSummary] =
    useState<InfrastructureMachinesSummaryResponse>();
  const [platformHealthSummary, setPlatformHealthSummary] =
    useState<PlatformHealthSummaryResponse>();
  const [platformAvailability, setPlatformAvailability] =
    useState<PlatformAvailabilityResponse>();
  const [platformAverageResponseTime, setPlatformAverageResponseTime] =
    useState<PlatformAverageResponseTimeResponse>();
  const [platformChecksSummary, setPlatformChecksSummary] =
    useState<PlatformChecksSummaryResponse>();
  const [isLoadingInfrastructureSummary, setIsLoadingInfrastructureSummary] =
    useState(true);
  const [isLoadingPlatformHealthSummary, setIsLoadingPlatformHealthSummary] =
    useState(true);
  const [isLoadingPlatformAvailability, setIsLoadingPlatformAvailability] =
    useState(true);
  const [
    isLoadingPlatformAverageResponseTime,
    setIsLoadingPlatformAverageResponseTime,
  ] = useState(true);
  const [isLoadingPlatformChecksSummary, setIsLoadingPlatformChecksSummary] =
    useState(true);

  useEffect(() => {
    async function getInfrastructureSummary() {
      try {
        const result = await api.get<InfrastructureMachinesSummaryResponse>(
          "/customer/infrastructure/machines/summary"
        );

        setInfrastructureSummary(result.data);
      } catch (err: any) {
        console.error(err);
      } finally {
        setIsLoadingInfrastructureSummary(false);
      }
    }

    getInfrastructureSummary();
  }, []);

  useEffect(() => {
    async function getPlatformHealthSummary() {
      try {
        const result = await api.get<PlatformHealthSummaryResponse>(
          "/customer/healthMonitor/summary"
        );

        setPlatformHealthSummary(result.data);
      } catch (err: any) {
        console.error(err);
      } finally {
        setIsLoadingPlatformHealthSummary(false);
      }
    }

    getPlatformHealthSummary();
  }, []);

  useEffect(() => {
    async function getPlatformAvailability() {
      try {
        const result = await api.get<PlatformAvailabilityResponse>(
          "/customer/healthMonitor/availability",
          {
            params: {
              hours: DASHBOARD_HEALTH_PERIOD_HOURS,
            },
          }
        );

        setPlatformAvailability(result.data);
      } catch (err: any) {
        console.error(err);
      } finally {
        setIsLoadingPlatformAvailability(false);
      }
    }

    getPlatformAvailability();
  }, []);

  useEffect(() => {
    async function getPlatformAverageResponseTime() {
      try {
        const result = await api.get<PlatformAverageResponseTimeResponse>(
          "/customer/healthMonitor/response-time/average",
          {
            params: {
              hours: DASHBOARD_HEALTH_PERIOD_HOURS,
            },
          }
        );

        setPlatformAverageResponseTime(result.data);
      } catch (err: any) {
        console.error(err);
      } finally {
        setIsLoadingPlatformAverageResponseTime(false);
      }
    }

    getPlatformAverageResponseTime();
  }, []);

  useEffect(() => {
    async function getPlatformChecksSummary() {
      try {
        const result = await api.get<PlatformChecksSummaryResponse>(
          "/customer/healthMonitor/checks/summary",
          {
            params: {
              hours: DASHBOARD_HEALTH_PERIOD_HOURS,
            },
          }
        );

        setPlatformChecksSummary(result.data);
      } catch (err: any) {
        console.error(err);
      } finally {
        setIsLoadingPlatformChecksSummary(false);
      }
    }

    getPlatformChecksSummary();
  }, []);

  const metrics: IDashboardMetricDTO[] = useMemo(
    () => [
      {
        title: "Infraestruturas",
        value: isLoadingInfrastructureSummary
          ? "..."
          : masks.formatMilhar(infrastructureSummary?.total || 0),
        subtitle: isLoadingInfrastructureSummary
          ? "Carregando ambientes"
          : `${masks.formatMilhar(
              infrastructureSummary?.online || 0
            )} online de ${masks.formatMilhar(
              infrastructureSummary?.total || 0
            )}`,
        icon: FaServer,
        variant: "info",
      },
      {
        title: "Plataformas Monitoradas",
        value: isLoadingPlatformHealthSummary
          ? "..."
          : masks.formatMilhar(platformHealthSummary?.total || 0),
        subtitle: isLoadingPlatformHealthSummary
          ? "Carregando plataformas"
          : `${masks.formatMilhar(
              platformHealthSummary?.online || 0
            )} online, ${masks.formatMilhar(
              platformHealthSummary?.attention || 0
            )} em atenção`,
        icon: FiGlobe,
        variant: "primary",
      },
      {
        title: "Disponibilidade das Plataformas",
        value: isLoadingPlatformAvailability
          ? "..."
          : `${(platformAvailability?.availability || 0)
              .toFixed(2)
              .replace(".", ",")}%`,
        subtitle: isLoadingPlatformAvailability
          ? "Carregando disponibilidade"
          : `${masks.formatMilhar(
              platformAvailability?.availableChecks || 0
            )} de ${masks.formatMilhar(
              platformAvailability?.totalChecks || 0
            )} checks disponíveis nas últimas ${DASHBOARD_HEALTH_PERIOD_HOURS}h`,
        icon: FaCheckCircle,
        variant: "success",
      },
      {
        title: "Resposta média das plataformas",
        value: isLoadingPlatformAverageResponseTime
          ? "..."
          : `${masks.formatMilhar(
              Math.round(
                platformAverageResponseTime?.averageResponseTime || 0
              )
            )} ms`,
        subtitle: isLoadingPlatformAverageResponseTime
          ? "Carregando resposta média"
          : `${masks.formatMilhar(
              platformAverageResponseTime?.totalChecks || 0
            )} checks nas últimas ${DASHBOARD_HEALTH_PERIOD_HOURS}h`,
        icon: FiWifi,
        variant: "warning",
      },
    ],
    [
      infrastructureSummary?.online,
      infrastructureSummary?.total,
      isLoadingInfrastructureSummary,
      isLoadingPlatformAverageResponseTime,
      isLoadingPlatformAvailability,
      isLoadingPlatformHealthSummary,
      platformAverageResponseTime?.averageResponseTime,
      platformAverageResponseTime?.totalChecks,
      platformHealthSummary?.attention,
      platformHealthSummary?.online,
      platformHealthSummary?.total,
      platformAvailability?.availability,
      platformAvailability?.availableChecks,
      platformAvailability?.totalChecks,
    ]
  );

  const operationalSummariesData: IDashboardMetricDTO[] = useMemo(
    () =>
      operationalSummaries.map((summary) => {
        if (summary.title.startsWith("Plataformas")) {
          return {
            ...summary,
            value: isLoadingPlatformHealthSummary
              ? "..."
              : masks.formatMilhar(platformHealthSummary?.attention || 0),
            subtitle: isLoadingPlatformHealthSummary
              ? "Carregando plataformas"
              : "Resposta alta ou instabilidade",
          };
        }

        if (!summary.title.startsWith("Checks")) {
          return summary;
        }

        return {
          ...summary,
          value: isLoadingPlatformChecksSummary
            ? "..."
            : masks.formatMilhar(platformChecksSummary?.totalChecks || 0),
          subtitle: isLoadingPlatformChecksSummary
            ? "Carregando checks"
            : `Consultas automáticas nas últimas ${DASHBOARD_HEALTH_PERIOD_HOURS}h`,
        };
      }),
    [
      isLoadingPlatformChecksSummary,
      isLoadingPlatformHealthSummary,
      platformChecksSummary?.totalChecks,
      platformHealthSummary?.attention,
    ]
  );

  return (
    <>
      <div>
        <h3 className="fw-bold mb-2">Dashboard</h3>
        <p className="text-muted mb-0">
          Resumo administrativo da saúde das plataformas, infras contratadas e
          plataformas hospedadas.
        </p>
      </div>

      <Card className="border-0 shadow-sm mb-4">
        <div className="card-body p-4">
          <div className="row align-items-center g-4">
            <div className="col-lg-8">
              <span className="badge bg-primary text-white mb-3 fs-6">
                Centro de monitoramento
              </span>
              <h2 className="fw-bold mb-3">
                Saúde operacional das plataformas em tempo real
              </h2>
              <p className="text-muted mb-0">
                Acompanhe disponibilidade, ping, tempo de resposta,
                infraestrutura contratada e distribuição das plataformas monitoradas
                em uma visão única para decisões rápidas.
              </p>
            </div>
            <div className="col-lg-4">
              <div className="row g-3">
                <div className="col-6">
                  <div className="p-3 bg-light rounded text-center">
                    <FiActivity className="text-success mb-2" size={26} />
                    <div className="fw-bold">Normal</div>
                    <small className="text-muted">Operação</small>
                  </div>
                </div>
                <div className="col-6">
                  <div className="p-3 bg-light rounded text-center">
                    <FiAlertTriangle className="text-warning mb-2" size={26} />
                    <div className="fw-bold">2 avisos</div>
                    <small className="text-muted">Acompanhar</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div className="row g-3 mb-4">
        {metrics.map((metric) => (
          <div key={metric.title} className="col-sm-6 col-xl-3">
            <DashboardMetricCard metric={metric} />
          </div>
        ))}
      </div>

      <div className="mb-4">
        <div className="mb-3">
          <h5 className="fw-bold mb-1">Resumo operacional</h5>
          <small className="text-muted">
            Indicadores rápidos para leitura administrativa da plataforma.
          </small>
        </div>
        <div className="row g-3">
          {operationalSummariesData.map((summary) => (
            <div key={summary.title} className="col-sm-6 col-xl-3">
              <DashboardMetricCard metric={summary} />
            </div>
          ))}
        </div>
      </div>

    </>
  );
}
